import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { MapPin, Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, CloudFog } from "lucide-react";

// ---------------------------------------------------------------------------
// Widget Registry
// ---------------------------------------------------------------------------
export const WIDGET_TYPES = [
  { id: "clock",     name: "Clock & Date",  icon: "🕐" },
  { id: "weather",   name: "Weather",       icon: "⛅" },
  { id: "ticker",    name: "News Ticker",   icon: "📰" },
  { id: "qr",        name: "QR Code",       icon: "▦"  },
  { id: "countdown", name: "Countdown",     icon: "⏱"  },
];

// Old Android-stick WebViews (Chrome < 105) don't support CSS Container
// Queries, so we stopped using `cqh`/`cqw` units. Instead we drive two CSS
// variables (`--cqh`, `--cqw`) from a ResizeObserver — 1 `--cqh` = 1% of the
// widget's height in pixels, 1 `--cqw` = 1% of its width. Every font-size /
// padding inside a widget now uses `calc(N * var(--cqh|w))`, which works in
// every browser back to ~2016.
const containerStyle = {};
// useCqVars: returns a ref to attach to the widget's outermost div. The hook
// watches the element's size and writes --cqh / --cqw as inline pixels.
//
// Robustness notes:
//   * Uses ResizeObserver when available (Chrome 64+, covers all modern
//     stick WebViews) so we react to live resize.
//   * Also runs `apply()` on first paint via requestAnimationFrame and one
//     more time on `window.resize` — guarantees correct dimensions even if
//     RO is missing or first measurement was 0 (layout-pending).
//   * If width/height is still 0 (rare race), `--cqw`/`--cqh` are set to a
//     safe default of 1px so child `calc()` expressions never collapse to
//     nothing — child text appears tiny instead of invisible.
function useCqVars() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const apply = () => {
      const r = el.getBoundingClientRect();
      const w = r.width  > 0 ? r.width  / 100 : 1;
      const h = r.height > 0 ? r.height / 100 : 1;
      el.style.setProperty("--cqw", `${w}px`);
      el.style.setProperty("--cqh", `${h}px`);
    };
    apply();
    // Re-measure after first paint — first render can be 0 if parent layout
    // wasn't ready when the ref attached.
    const raf = requestAnimationFrame(apply);
    let ro;
    try {
      ro = new ResizeObserver(apply);
      ro.observe(el);
    } catch (_e) { /* Old WebView w/out ResizeObserver — fallback below */ }
    window.addEventListener("resize", apply);
    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, []);
  return ref;
}

const isTransparentBg = (v) => !v || v === "transparent";

// ---------------------------------------------------------------------------
// Clock
// ---------------------------------------------------------------------------
function ClockWidget({ config }) {
  const ref = useCqVars();
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const showSeconds = !!config.show_seconds;
  const is24 = !!config.format_24h;
  const time = is24
    ? now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: showSeconds ? "2-digit" : undefined })
    : now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: showSeconds ? "2-digit" : undefined, hour12: true });
  const m = time.match(/(.*?)\s?(AM|PM)?$/i);
  const timePart = m ? m[1] : time;
  const ampm = m && m[2] ? m[2].toUpperCase() : "";
  const showDate = config.show_date !== false;
  const fg = config.fg || "#0F172A";
  const bg = isTransparentBg(config.bg) ? "transparent" : config.bg;

  return (
    <div
      ref={ref}
      className="w-full h-full flex flex-col items-center justify-center overflow-hidden"
      style={{ ...containerStyle, background: bg, color: fg, fontFamily: "Outfit, sans-serif", padding: "calc(4 * var(--cqh)) calc(3 * var(--cqw))" }}
    >
      {showDate && (
        <div className="uppercase font-bold mb-1 truncate max-w-full" style={{ fontSize: "calc(11 * var(--cqh))", letterSpacing: "0.14em", color: `${fg}AA` }}>
          {now.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
        </div>
      )}
      <div className="flex items-baseline leading-none max-w-full nx-gap" style={{ "--nx-gap": "0.5rem" }}>
        <div className="font-extrabold tracking-tight truncate" style={{ fontSize: showDate ? "calc(44 * var(--cqh))" : "calc(62 * var(--cqh))" }}>{timePart}</div>
        {ampm && (
          <div className="font-bold flex-shrink-0" style={{ fontSize: showDate ? "calc(16 * var(--cqh))" : "calc(22 * var(--cqh))", color: `${fg}99` }}>{ampm}</div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weather
// ---------------------------------------------------------------------------
const WEATHER_LABEL = {
  0: "Clear sky",  1: "Mainly clear",  2: "Partly cloudy",  3: "Overcast",
  45: "Foggy",     48: "Foggy",
  51: "Drizzle",   53: "Drizzle",      55: "Drizzle",
  61: "Light rain",63: "Rain",         65: "Heavy rain",
  71: "Snow",      73: "Snow",         75: "Heavy snow",   77: "Snow grains",
  80: "Showers",   81: "Showers",      82: "Heavy showers",
  95: "Thunder",   96: "Thunder hail", 99: "Thunder hail",
};

function WeatherIcon({ code, color = "#FFFFFF", sizeCqh = 44, realistic = true, animated = true }) {
  const c = code ?? 1;
  // Soft drop shadow — kept small enough that it doesn't bleed past the
  // widget's overflow:hidden box even when the icon sits near an edge.
  const filter = realistic ? "drop-shadow(0 calc(1.5 * var(--cqh)) calc(3 * var(--cqh)) rgba(0,0,0,0.22))" : "none";
  const sz = `calc(${sizeCqh} * var(--cqh))`;
  // Pick the animation class based on weather code so each icon "feels" alive.
  // The keyframes are defined once at the bottom of this file (WeatherKeyframes).
  let animClass = "";
  if (animated) {
    if (c === 0 || c === 1) animClass = "nx-anim-sun";       // gentle rotate
    else if (c <= 3)        animClass = "nx-anim-cloud";     // horizontal drift
    else if (c <= 48)       animClass = "nx-anim-cloud";
    else if (c <= 67 || (c >= 80 && c <= 82)) animClass = "nx-anim-rain";  // drop with rain feel
    else if (c <= 77)       animClass = "nx-anim-snow";      // soft float
    else if (c >= 95)       animClass = "nx-anim-thunder";   // flicker
    else                    animClass = "nx-anim-cloud";
  }
  const props = {
    color,
    strokeWidth: 1.6,
    className: animClass,
    style: { width: sz, height: sz, filter },
  };
  if (c === 0 || c === 1) return <Sun {...props} />;
  if (c <= 3)  return <Cloud {...props} />;
  if (c <= 48) return <CloudFog {...props} />;
  if (c <= 57) return <CloudDrizzle {...props} />;
  if (c <= 67 || (c >= 80 && c <= 82)) return <CloudRain {...props} />;
  if (c <= 77) return <CloudSnow {...props} />;
  if (c >= 95) return <CloudLightning {...props} />;
  return <Sun {...props} />;
}

// Keyframes & helper classes for the weather animations. Injected once.
function WeatherKeyframes() {
  return (
    <style>{`
      @keyframes nx-sun-spin   { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes nx-cloud-drift { 0% { transform: translateX(0); } 50% { transform: translateX(4%); } 100% { transform: translateX(0); } }
      @keyframes nx-rain-pulse { 0%,100% { transform: translateY(0); opacity: 1; } 50% { transform: translateY(4%); opacity: 0.85; } }
      @keyframes nx-snow-float { 0%,100% { transform: translateY(0) rotate(-3deg); } 50% { transform: translateY(3%) rotate(3deg); } }
      @keyframes nx-thunder-flick { 0%,92%,100% { opacity: 1; } 94% { opacity: 0.5; } 96% { opacity: 1; } 98% { opacity: 0.6; } }
      .nx-anim-sun     { animation: nx-sun-spin   60s linear infinite; transform-origin: center; }
      .nx-anim-cloud   { animation: nx-cloud-drift 8s ease-in-out infinite; }
      .nx-anim-rain    { animation: nx-rain-pulse 1.6s ease-in-out infinite; }
      .nx-anim-snow    { animation: nx-snow-float 5s ease-in-out infinite; }
      .nx-anim-thunder { animation: nx-thunder-flick 4s ease-in-out infinite; }

      /* Drifting background clouds inside the weather widget — purely decorative. */
      @keyframes nx-bgcloud-1 { from { transform: translateX(-15%); } to { transform: translateX(115%); } }
      @keyframes nx-bgcloud-2 { from { transform: translateX(115%); } to { transform: translateX(-15%); } }
      .nx-bgcloud-1 { animation: nx-bgcloud-1 38s linear infinite; }
      .nx-bgcloud-2 { animation: nx-bgcloud-2 56s linear infinite; }
    `}</style>
  );
}

async function geocodeCity(city, signal) {
  if (!city) return null;
  const key = city.trim().toLowerCase();

  // 1. Module-level cache (fast in-memory hit).
  if (geocodeCity._cache && geocodeCity._cache[key]) return geocodeCity._cache[key];

  // 2. Persistent localStorage cache. Survives page reload / WebView restart
  //    so old TV WebViews that occasionally fail to reach the geocoding API
  //    can still resolve the configured city.
  let lsCached = null;
  try {
    const raw = localStorage.getItem("nexso_geo:" + key);
    if (raw) lsCached = JSON.parse(raw);
  } catch (_e) { /* localStorage disabled — skip */ }
  if (lsCached) {
    geocodeCity._cache = geocodeCity._cache || {};
    geocodeCity._cache[key] = lsCached;
  }

  // 3. Always try a fresh network fetch — but if it fails and we have a
  //    cached value from a previous successful fetch, return that instead
  //    of null. This is the difference between "weather widget invisible
  //    on old WebView" and "weather widget shows last known data".
  try {
    const r = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city)}&count=1&language=en&format=json`,
      { signal }
    );
    if (!r.ok) return lsCached || null;
    const j = await r.json();
    const hit = j?.results?.[0];
    if (hit) {
      const v = { lat: hit.latitude, lon: hit.longitude, name: hit.name };
      geocodeCity._cache = geocodeCity._cache || {};
      geocodeCity._cache[key] = v;
      try { localStorage.setItem("nexso_geo:" + key, JSON.stringify(v)); }
      catch (_e) { /* localStorage full / disabled */ }
      return v;
    }
  } catch (e) {
    if (e.name === "AbortError") throw e;
    return lsCached || null;
  }
  return lsCached || null;
}
geocodeCity._cache = {};

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function WeatherWidget({ config }) {
  const ref = useCqVars();
  const [now, setNow] = useState(null);
  const [daily, setDaily] = useState([]);
  const [err, setErr] = useState(false);
  const [resolvedCity, setResolvedCity] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const city = (config.city || "New York").trim();
  const unit = config.unit || "fahrenheit";
  const days = Math.max(1, Math.min(7, config.forecast_days || 1));
  const showHilo = config.show_hilo !== false;
  const realistic = config.realistic_icon !== false;
  const bg = isTransparentBg(config.bg) ? "transparent" : (config.bg || "linear-gradient(135deg, #38BDF8 0%, #0EA5E9 55%, #0284C7 100%)");
  const fg = config.fg || "#FFFFFF";

  // Refresh every 15 min by bumping a tick state (which is in deps below)
  useEffect(() => {
    const t = setInterval(() => setRefreshTick((x) => x + 1), 1000 * 60 * 15);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    setErr(false);
    // Debounce so fast typing in the city field doesn't slam the API.
    const timer = setTimeout(async () => {
      try {
        // Resolve lat/lon for `city`. Strategy:
        //   1. Try to geocode (fresh result — supports the user typing a
        //      new city in the dashboard).
        //   2. If geocoding is unreachable (old TV WebViews sometimes block
        //      cross-origin calls / fail TLS to geocoding-api.open-meteo.com),
        //      FALL BACK to any `config.lat` / `config.lon` already stored
        //      from a prior successful geocode. This is what keeps the
        //      widget visible on the customer's TV.
        //   3. If geocoding fails AND we have no stored fallback, give up.
        let lat = null;
        let lon = null;
        let resolved = city;
        const geo = await geocodeCity(city, ctrl.signal);
        if (geo) {
          lat = geo.lat;
          lon = geo.lon;
          resolved = geo.name || city;
        } else if (config.lat != null && config.lon != null) {
          lat = config.lat;
          lon = config.lon;
        } else {
          if (ctrl.signal.aborted) return;
          setErr(true);
          setResolvedCity("");
          return;
        }
        const url =
          `https://api.open-meteo.com/v1/forecast` +
          `?latitude=${lat}&longitude=${lon}` +
          `&current=temperature_2m,weather_code` +
          `&daily=temperature_2m_max,temperature_2m_min,weather_code` +
          `&temperature_unit=${unit}&timezone=auto&forecast_days=${Math.max(days, 1)}`;
        const r = await fetch(url, { signal: ctrl.signal });
        if (!r.ok) throw new Error("api");
        const j = await r.json();
        if (j?.current?.temperature_2m == null) throw new Error("nodata");
        setNow({ temp: j.current.temperature_2m, code: j.current.weather_code });
        const d = j.daily || {};
        setDaily((d.time || []).map((dt, i) => ({
          date: new Date(dt),
          hi: d.temperature_2m_max?.[i],
          lo: d.temperature_2m_min?.[i],
          code: d.weather_code?.[i],
        })));
        setResolvedCity(resolved);
        setErr(false);
      } catch (e) {
        if (e.name === "AbortError") return;
        setErr(true);
        setResolvedCity("");
        // Only seed demo data on first load — don't overwrite real data.
        setNow((prev) => prev || { temp: unit === "celsius" ? 22 : 72, code: 1 });
        setDaily((prev) => prev.length > 0 ? prev : [
          { date: new Date(),                        hi: 75, lo: 60, code: 1 },
          { date: new Date(Date.now() + 1*86400000), hi: 73, lo: 58, code: 2 },
          { date: new Date(Date.now() + 2*86400000), hi: 68, lo: 55, code: 61 },
          { date: new Date(Date.now() + 3*86400000), hi: 70, lo: 56, code: 1 },
          { date: new Date(Date.now() + 4*86400000), hi: 76, lo: 62, code: 0 },
          { date: new Date(Date.now() + 5*86400000), hi: 79, lo: 65, code: 0 },
          { date: new Date(Date.now() + 6*86400000), hi: 78, lo: 64, code: 2 },
        ]);
      }
    }, 350);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [city, unit, days, config.lat, config.lon, refreshTick]);

  const temp = now?.temp != null ? Math.round(now.temp) : null;
  const code = now?.code;
  const label = code != null ? (WEATHER_LABEL[code] || "Mainly clear") : "Loading…";

  return (
    <div
      ref={ref}
      className="w-full h-full relative overflow-hidden flex flex-col"
      style={{ ...containerStyle, background: bg, color: fg, fontFamily: "Outfit, sans-serif" }}
    >
      <WeatherKeyframes />
      {/* Sun glow — only when bg isn't transparent */}
      {!isTransparentBg(config.bg) && (
        <>
          <div
            aria-hidden="true"
            className="absolute rounded-full pointer-events-none"
            style={{
              top: "-30%", right: "-20%", width: "60%", height: "100%",
              background: "radial-gradient(circle, rgba(255,222,128,0.7), transparent 70%)",
            }}
          />
          {/* Drifting decorative clouds — only render when not snow/thunder so
              they don't clash with the foreground. Constrained with maxHeight
              so the cloud SVG can never bleed past the bottom of the widget. */}
          {(code == null || (code <= 3) || (code >= 0 && code <= 48)) && (
            <>
              <div
                aria-hidden="true"
                className="absolute pointer-events-none nx-bgcloud-1"
                style={{ top: "12%", left: 0, width: "26%", maxHeight: "32%", opacity: 0.35, overflow: "hidden" }}
              >
                <Cloud color={fg} strokeWidth={1.4} style={{ width: "100%", height: "auto", display: "block" }} />
              </div>
              <div
                aria-hidden="true"
                className="absolute pointer-events-none nx-bgcloud-2"
                style={{ top: "52%", left: 0, width: "22%", maxHeight: "28%", opacity: 0.25, overflow: "hidden" }}
              >
                <Cloud color={fg} strokeWidth={1.4} style={{ width: "100%", height: "auto", display: "block" }} />
              </div>
            </>
          )}
        </>
      )}

      {/* TODAY mode */}
      {days === 1 && (
        <div className="relative flex-1 flex flex-col justify-between min-w-0" style={{ padding: "calc(7 * var(--cqh)) calc(5 * var(--cqw))" }}>
          <div className="flex items-center min-w-0 nx-gap" style={{ "--nx-gap": "calc(2 * var(--cqw))" }}>
            <MapPin style={{ width: "calc(11 * var(--cqh))", height: "calc(11 * var(--cqh))", flexShrink: 0 }} strokeWidth={2.2} />
            <div className="font-semibold truncate min-w-0" style={{ fontSize: "calc(13 * var(--cqh))" }}>{resolvedCity || city}</div>
          </div>
          {err && !now ? (
            <div className="font-semibold" style={{ fontSize: "calc(12 * var(--cqh))", opacity: 0.9 }}>
              City &ldquo;{city}&rdquo; not found
            </div>
          ) : (
            <div className="flex items-end justify-between min-w-0 nx-gap" style={{ "--nx-gap": "calc(2 * var(--cqw))" }}>
              <div className="leading-none min-w-0 flex-1 overflow-hidden">
                <div className="font-extrabold leading-none truncate" style={{ fontSize: "calc(46 * var(--cqh))" }}>
                  {temp != null ? `${temp}°` : "—"}
                </div>
                <div className="font-medium truncate" style={{ fontSize: "calc(11 * var(--cqh))", marginTop: "calc(2 * var(--cqh))", opacity: 0.92 }}>{label}</div>
                {showHilo && daily[0] && (
                  <div className="font-semibold truncate" style={{ fontSize: "calc(9 * var(--cqh))", marginTop: "calc(1 * var(--cqh))", opacity: 0.85 }}>
                    H: {Math.round(daily[0].hi)}° · L: {Math.round(daily[0].lo)}°
                  </div>
                )}
              </div>
              {/* Icon wrapper: explicit max-size + overflow:hidden so the drop-shadow
                  and rotation animation never escape the widget bounds. */}
              <div
                className="opacity-95 flex-shrink-0 flex items-center justify-center"
                style={{ width: "calc(40 * var(--cqh))", height: "calc(40 * var(--cqh))", overflow: "visible" }}
              >
                <WeatherIcon code={code} color={fg} sizeCqh={38} realistic={realistic} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* MULTI-DAY mode (3 or 7 day) — fonts scale inversely with column count
          so 7 columns don't overflow their narrow tracks. Sizes are in cqw so
          they shrink with the widget width as well. */}
      {days > 1 && (() => {
        // Column-aware sizing. We use cqw so width drives readability:
        //   7-day → ~14% of width per column → smaller fonts
        //   3-day → ~33% of width per column → larger fonts
        const colW = 100 / days;            // % of widget width per column
        const dayLabelSize = Math.min(5.5, Math.max(2.4, colW * 0.30)); // cqw
        const tempSize     = Math.min(7.5, Math.max(3.0, colW * 0.45)); // cqw
        const lowTempSize  = Math.min(5.0, Math.max(2.4, colW * 0.32)); // cqw
        const iconSizeCqh  = days >= 7 ? 14 : days >= 5 ? 17 : 22;
        return (
          <div className="relative flex flex-col flex-1 min-w-0" style={{ padding: "calc(5 * var(--cqh)) calc(3 * var(--cqw))" }}>
            {/* Header strip — city + today */}
            <div className="flex items-center justify-between mb-2 min-w-0 nx-gap" style={{ "--nx-gap": "calc(2 * var(--cqw))" }}>
              <div className="flex items-center min-w-0 overflow-hidden nx-gap" style={{ "--nx-gap": "0.5rem" }}>
                <MapPin style={{ width: "calc(9 * var(--cqh))", height: "calc(9 * var(--cqh))", flexShrink: 0 }} strokeWidth={2.2} />
                <div className="font-semibold truncate min-w-0" style={{ fontSize: "calc(9 * var(--cqh))" }}>{resolvedCity || city}</div>
              </div>
              {temp != null && (
                <div className="flex items-center flex-shrink-0 nx-gap" style={{ "--nx-gap": "calc(1.5 * var(--cqw))" }}>
                  <div className="font-extrabold leading-none" style={{ fontSize: "calc(22 * var(--cqh))" }}>{temp}°</div>
                  <WeatherIcon code={code} color={fg} sizeCqh={18} realistic={realistic} />
                </div>
              )}
            </div>
            {/* Forecast row */}
            <div className="flex-1 flex justify-between items-stretch nx-gap" style={{ "--nx-gap": "calc(1 * var(--cqw))" }}>
              {daily.slice(0, days).map((d, i) => (
                <div key={i} className="flex flex-col items-center justify-center flex-1 min-w-0 overflow-hidden">
                  <div
                    className="font-bold uppercase opacity-85 w-full text-center"
                    style={{
                      fontSize: `calc(${dayLabelSize} * var(--cqw))`,
                      letterSpacing: "0.04em",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "clip",
                    }}
                  >
                    {DAY_NAMES[d.date.getDay()]}
                  </div>
                  <div style={{ marginTop: "calc(2 * var(--cqh))", marginBottom: "calc(2 * var(--cqh))" }}>
                    <WeatherIcon code={d.code} color={fg} sizeCqh={iconSizeCqh} realistic={realistic} />
                  </div>
                  <div className="font-bold" style={{ fontSize: `calc(${tempSize} * var(--cqw))`, lineHeight: 1 }}>{Math.round(d.hi)}°</div>
                  <div className="opacity-70 font-semibold" style={{ fontSize: `calc(${lowTempSize} * var(--cqw))`, marginTop: "calc(1 * var(--cqh))" }}>{Math.round(d.lo)}°</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ticker
// ---------------------------------------------------------------------------
function TickerWidget({ config }) {
  const ref = useCqVars();
  const items = (config.items && config.items.length > 0) ? config.items : ["Welcome to Nexso digital signage"];
  const speed = Number(config.speed) || 60;
  const text = items.join("    •    ");
  const bg = isTransparentBg(config.bg) ? "transparent" : (config.bg || "#0EA5E9");
  const fg = config.fg || "#FFFFFF";
  const fontPct = Math.max(10, Math.min(100, config.font_size_pct ?? 55));
  return (
    <div
      ref={ref}
      className="w-full h-full overflow-hidden flex items-center"
      style={{ ...containerStyle, background: bg, color: fg }}
    >
      <div
        className="font-bold uppercase tracking-widest whitespace-nowrap"
        style={{
          fontSize: `calc(${fontPct} * var(--cqh))`,
          animation: `nx-ticker ${speed}s linear infinite`,
          paddingLeft: "100%",
          fontFamily: "Outfit, sans-serif",
        }}
      >
        {text} &nbsp;&nbsp;&nbsp; • &nbsp;&nbsp;&nbsp; {text}
      </div>
      <style>{`@keyframes nx-ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QR
// ---------------------------------------------------------------------------
function QRWidget({ config }) {
  const ref = useCqVars();
  const value = config.value || "https://nexso.net";
  const label = config.label || "Scan for menu";
  const bg = isTransparentBg(config.bg) ? "transparent" : (config.bg || "#FFFFFF");
  const fg = config.fg || "#0F172A";
  const qrColor = config.qr_color || "#0F172A";
  const showLabel = config.show_label !== false && !!label;
  return (
    <div
      ref={ref}
      className="w-full h-full flex items-center overflow-hidden nx-gap"
      style={{ ...containerStyle, background: bg, color: fg, fontFamily: "Outfit, sans-serif", padding: "calc(5 * var(--cqh)) calc(4 * var(--cqw))", "--nx-gap": "calc(4 * var(--cqw))" }}
    >
      {/* QR size bound by BOTH height and width so it never overflows a narrow
          or short widget. min(calc(80 * var(--cqh)), calc(40 * var(--cqw))) keeps the code square. */}
      <div
        className="rounded-md flex-shrink-0 flex items-center justify-center"
        style={{
          width: "min(calc(82 * var(--cqh)), calc(42 * var(--cqw)))",
          height: "min(calc(82 * var(--cqh)), calc(42 * var(--cqw)))",
          padding: "calc(3 * var(--cqh))",
          background: "#FFFFFF",
        }}
      >
        <QRCodeSVG value={value} className="w-full h-full" includeMargin={false} bgColor="#FFFFFF" fgColor={qrColor} level="M" />
      </div>
      {showLabel && (
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="font-bold uppercase opacity-60 truncate" style={{ fontSize: "calc(10 * var(--cqh))", letterSpacing: "0.15em", marginBottom: "calc(2 * var(--cqh))" }}>Scan</div>
          <div className="font-bold leading-tight" style={{ fontSize: "calc(18 * var(--cqh))", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }}>{label}</div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Countdown
// ---------------------------------------------------------------------------
function CountdownWidget({ config }) {
  const ref = useCqVars();
  const target = config.target_iso ? new Date(config.target_iso) : new Date(Date.now() + 1000 * 60 * 60 * 24);
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const diff = Math.max(0, target - now);
  const totalSec = Math.floor(diff / 1000);
  const allUnits = {
    days:    { v: Math.floor(diff / (1000 * 60 * 60 * 24)),    label: "Days" },
    hours:   { v: Math.floor((diff / (1000 * 60 * 60)) % 24),  label: "Hrs" },
    minutes: { v: Math.floor((diff / (1000 * 60)) % 60),       label: "Min" },
    seconds: { v: Math.floor((diff / 1000) % 60),              label: "Sec" },
  };
  const enabledUnits = (config.units && config.units.length > 0) ? config.units : ["days", "hours", "minutes", "seconds"];
  const visible = enabledUnits.filter((u) => allUnits[u]).map((u) => ({ ...allUnits[u], key: u }));
  const label = config.label || "Limited offer ends in";
  const showLabel = !!label;
  const showUnitLabels = config.show_unit_labels !== false;
  const accent = config.accent || "#0EA5E9";
  const bg = isTransparentBg(config.bg) ? "transparent" : (config.bg || "#FFFFFF");
  const fg = config.fg || "#0F172A";
  const numCount = visible.length || 1;
  // Width-aware sizing so a 4-unit countdown can't run off a narrow widget.
  // Each unit takes ~2 chars of width; we cap by both cqw and cqh.
  const colW = 100 / Math.max(numCount, 1);
  const numSizeCqw = Math.min(18, colW * 0.55);   // never more than calc(18 * var(--cqw))
  const numSizeCqh = numCount <= 1 ? 55 : numCount === 2 ? 50 : numCount === 3 ? 42 : 36;

  return (
    <div
      ref={ref}
      className="w-full h-full flex flex-col items-center justify-center overflow-hidden"
      style={{ ...containerStyle, background: bg, color: fg, fontFamily: "Outfit, sans-serif", padding: "calc(5 * var(--cqh)) calc(3 * var(--cqw))" }}
    >
      {showLabel && (
        <div className="font-bold uppercase opacity-75 text-center truncate max-w-full" style={{ fontSize: "calc(10 * var(--cqh))", letterSpacing: "0.15em" }}>{label}</div>
      )}
      <div className="flex w-full justify-center nx-gap" style={{ "--nx-gap": "calc(2 * var(--cqw))", marginTop: showLabel ? "calc(4 * var(--cqh))" : 0 }}>
        {visible.map((u) => (
          <div key={u.key} className="text-center min-w-0 flex-1 overflow-hidden">
            <div
              className="font-extrabold leading-none tracking-tighter"
              style={{ fontSize: `min(calc(${numSizeCqh} * var(--cqh)), calc(${numSizeCqw} * var(--cqw)))`, color: accent }}
            >
              {String(u.v).padStart(2, "0")}
            </div>
            {showUnitLabels && (
              <div className="font-bold uppercase opacity-60 truncate" style={{ fontSize: "calc(9 * var(--cqh))", marginTop: "calc(1 * var(--cqh))" }}>{u.label}</div>
            )}
          </div>
        ))}
        {visible.length === 0 && (
          <div className="font-extrabold leading-none" style={{ fontSize: "min(calc(46 * var(--cqh)), calc(16 * var(--cqw)))", color: accent }}>
            {String(totalSec).padStart(2, "0")}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WidgetOverlay (TV display) + SingleWidget (canvas elements)
// ---------------------------------------------------------------------------
export function WidgetOverlay({ widgets = [] }) {
  if (!widgets || widgets.length === 0) return null;
  return (
    <div
      className="absolute pointer-events-none"
      style={{ top: 0, right: 0, bottom: 0, left: 0 }}
      data-testid="widget-overlay"
    >
      {widgets.map((w) => {
        const W = pickWidget(w.type);
        if (!W) return null;
        return (
          <div
            key={w.id}
            className="absolute overflow-hidden"
            style={{
              left: `${w.x ?? 70}%`,
              top:  `${w.y ?? 5}%`,
              width:  `${w.w ?? 25}%`,
              height: `${w.h ?? 12}%`,
              borderRadius: "calc(1 * var(--cqw))",
              boxShadow: isTransparentBg((w.config || {}).bg) ? "none" : "0 8px 24px rgba(15,23,42,0.18)",
              pointerEvents: "auto",
            }}
            data-testid={`widget-${w.type}`}
          >
            <W config={w.config || {}} />
          </div>
        );
      })}
    </div>
  );
}

export function SingleWidget({ type, config }) {
  const W = pickWidget(type);
  if (!W) return null;
  return <W config={config || {}} />;
}

function pickWidget(type) {
  switch (type) {
    case "clock":     return ClockWidget;
    case "weather":   return WeatherWidget;
    case "ticker":    return TickerWidget;
    case "qr":        return QRWidget;
    case "countdown": return CountdownWidget;
    default:          return null;
  }
}

export function defaultWidgetLayout(type) {
  switch (type) {
    case "clock":     return { x: 78, y: 4,  w: 18, h: 12 };
    case "weather":   return { x: 4,  y: 4,  w: 22, h: 14 };
    case "ticker":    return { x: 0,  y: 92, w: 100, h: 8 };
    case "qr":        return { x: 80, y: 70, w: 16, h: 22 };
    case "countdown": return { x: 35, y: 82, w: 30, h: 14 };
    default:          return { x: 50, y: 50, w: 20, h: 10 };
  }
}

export function defaultWidgetConfig(type) {
  switch (type) {
    case "clock":     return { format_24h: false, show_seconds: false, show_date: true, bg: "#FFFFFF", fg: "#0F172A" };
    case "weather":   return { city: "New York", unit: "fahrenheit", forecast_days: 1, show_hilo: true, realistic_icon: true };
    case "ticker":    return { items: ["Welcome — try our daily special!"], speed: 60, font_size_pct: 55, bg: "#0EA5E9", fg: "#FFFFFF" };
    case "qr":        return { value: "https://nexso.net", label: "Scan for menu", show_label: true, qr_color: "#0F172A", bg: "#FFFFFF", fg: "#0F172A" };
    case "countdown": return {
      target_iso: new Date(Date.now() + 1000 * 60 * 60 * 24 * 3).toISOString(),
      label: "Sale ends in",
      units: ["days", "hours", "minutes", "seconds"],
      show_unit_labels: true,
      accent: "#0EA5E9", bg: "#FFFFFF", fg: "#0F172A",
    };
    default: return {};
  }
}

// Standalone marketing cards (Landing hero)
export function MiniWeatherCard({ city = "New York", temp = 72, label = "Sunny" }) {
  return (
    <div className="relative overflow-hidden rounded-2xl text-white p-5 shadow-xl"
         style={{ background: "linear-gradient(135deg, #38BDF8 0%, #0EA5E9 55%, #0284C7 100%)", fontFamily: "Outfit, sans-serif" }}>
      <div aria-hidden="true" className="absolute -top-12 -right-12 w-44 h-44 rounded-full"
           style={{ background: "radial-gradient(circle, rgba(255,222,128,0.7), transparent 70%)" }} />
      <div className="relative flex items-center gap-1.5 text-sm font-semibold opacity-95">
        <MapPin className="h-3.5 w-3.5" /> {city}
      </div>
      <div className="relative mt-3 flex items-end justify-between">
        <div>
          <div className="text-5xl font-extrabold leading-none">{temp}°</div>
          <div className="text-xs font-medium mt-1.5 opacity-90">{label}</div>
        </div>
        <Sun className="h-12 w-12 opacity-95" strokeWidth={1.6} />
      </div>
    </div>
  );
}

export function MiniClockCard() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const time = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  const m = time.match(/(.*?)\s?(AM|PM)$/i);
  const tp = m ? m[1] : time;
  const ap = m && m[2] ? m[2].toUpperCase() : "";
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-xl" style={{ fontFamily: "Outfit, sans-serif" }}>
      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">{now.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <div className="text-4xl font-extrabold tracking-tight text-slate-900 leading-none">{tp}</div>
        <div className="text-sm font-bold text-slate-500">{ap}</div>
      </div>
    </div>
  );
}
